<script setup lang="ts">
import RepairSection from '@/components/rc/miner-repairs/repairs-sidebar/RepairSection.vue'
import { useMinerDetails } from '@/composables/rc/miner-details'
import { useMinerRepairActions } from '@/composables/rc/miner-repair-actions'
import { HiddenRepairServices } from '@/types/rc/services'
import type { IService } from '@/types/rc/work-orders'
import { Icon } from '@iconify/vue'
import { computed, ref, toRefs, watchEffect } from 'vue'

const props = defineProps<{ minerId: number; services: IService[] }>()

// Server state bindings
const { minerId } = toRefs(props)
const { minerDetailsQuery } = useMinerDetails(minerId)
const { data: minerDetails } = minerDetailsQuery

const visibleServices = computed(() =>
  props.services.filter((s: IService) => !HiddenRepairServices.includes(s.description))
)

const isActive = computed(() => (minerDetails.value?.performed_services.length ?? 0) == props.services.length)

// UI state
const previousSelectedServices = ref<number[]>(minerDetails.value?.performed_services.map((s: IService) => s.id) ?? [])
const currentSelectedServices = ref<number[]>(previousSelectedServices.value)

watchEffect(() => {
  // Keep services synced
  const services = minerDetailsQuery.data.value?.performed_services
  if (services) {
    const serviceIds = services.map((s: IService) => s.id)
    previousSelectedServices.value = serviceIds
    currentSelectedServices.value = serviceIds
  }
})

const { updatePerformedServicesStateMutation } = useMinerRepairActions(minerId)
const { mutate: mutatePerformedServices, isPending: mutatePerformedServicesPending } =
  updatePerformedServicesStateMutation

const onCheckChange = () => {
  mutatePerformedServices(
    { performed_services: currentSelectedServices.value },
    {
      onSuccess: () => {
        previousSelectedServices.value = currentSelectedServices.value
      },
      onError: () => {
        currentSelectedServices.value = previousSelectedServices.value
      },
    }
  )
}
</script>

<template>
  <RepairSection title="Perf. Services" :step="1" :active="!!isActive">
    <el-checkbox-group
      v-model="currentSelectedServices"
      class="flex flex-col"
      :disabled="mutatePerformedServicesPending"
      @change="onCheckChange"
    >
      <el-checkbox v-for="svc in visibleServices" :key="svc.id" :label="svc.description" :value="svc.id">
        <div class="flex flex-row items-center gap-1">
          <Icon :icon="svc.icon" :width="20" />
          <span>{{ svc.description }}</span>
        </div>
      </el-checkbox>
    </el-checkbox-group>
  </RepairSection>
</template>
